import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { COURT_FINDER_PAGE } from 'constants/pages';
import { SportsEnum } from 'types/generated/client';
import { useGeoLocation } from 'hooks/useGeoLocation';
import CourtMapMarker from 'components/Explore/Courts/CourtMapMarker';
import { GoogleMapProps } from 'components/GoogleMap/props';
import CityVenueList from './CityVenueList';
import { Props } from './types';

interface UseCityVenuesProps {
  city: Props['city'];
  nearbyVenues: Props['nearbyVenues'];
}

const useCityVenues = ({ city, nearbyVenues }: UseCityVenuesProps) => {
  const router = useRouter();
  const { centerLatitude, centerLongitude, isExactCenter } = useGeoLocation();
  const [listHoveredVenueId, setListHoveredVenueId] = useState<string | null>(null);
  const [activeVenueId, setActiveVenueId] = useState<string | null>(null);
  const [distance, setDistance] = useState({ id: 25, name: '25 miles' });

  // Combine city venues and nearby venues for map display
  const allVenues = useMemo(() => {
    const cityVenues = city?.venues || [];
    const nearby = nearbyVenues || [];

    // Create a Set to avoid duplicates
    const venueIds = new Set();
    const combined = [];

    // Add city venues first
    for (const venue of cityVenues) {
      if (venue?.id && !venueIds.has(venue.id)) {
        venueIds.add(venue.id);
        combined.push({
          ...venue,
          isNearby: false,
        });
      }
    }

    // Add nearby venues
    for (const venue of nearby) {
      if (venue?.id && !venueIds.has(venue.id)) {
        venueIds.add(venue.id);
        combined.push({
          ...venue,
          isNearby: true,
        });
      }
    }

    return combined;
  }, [city?.venues, nearbyVenues]);

  const activeVenue = allVenues.find((venue) => venue.id === activeVenueId);

  const handleNavigateToCourtFinder = (coords?: { lat: number; lng: number }) => {
    router.push(
      `${COURT_FINDER_PAGE}?lat=${coords?.lat.toFixed(6)}&lng=${coords?.lng.toFixed(
        6,
      )}&sport=${SportsEnum.Pickleball.toLowerCase()}`,
    );
  };

  const courtMapMarkers = useMemo(() => {
    return allVenues.map((venue) => {
      const venueWithCoords = venue as any;
      const activeVenueWithCoords = activeVenue as any;
      return {
        zIndex: activeVenue?.id === venue.id ? 2 : 1,
        lat: venueWithCoords?.geometry?.coordinates?.[1] || 0,
        lng: venueWithCoords?.geometry?.coordinates?.[0] || 0,
        markerId: venue.id,
        children: (
          <CourtMapMarker
            venue={venueWithCoords}
            activeVenue={activeVenueWithCoords}
            setActiveVenueId={setActiveVenueId}
            listHoveredVenueId={listHoveredVenueId}
            setListHoveredVenueId={setListHoveredVenueId}
          />
        ),
      };
    });
  }, [allVenues, activeVenueId, listHoveredVenueId]);

  // console.log({ courtMapMarkers: courtMapMarkers });

  const courtMapProps: GoogleMapProps = {
    syncDragToURL: false,
    onCenterChange(center) {
      handleNavigateToCourtFinder({ lat: center.lat, lng: center.lng });
    },
    center: {
      lat: city?.latitude || centerLatitude || 0,
      lng: city?.longitude || centerLongitude || 0,
    },
    zoom: 12,
    centerIcon: (
      <div className="h-8 w-8 rounded-full border-[12px] border-color-border-brand bg-white" />
    ),
    markers: courtMapMarkers,
    isExactCenter,
  };

  return {
    VenueList: (
      <CityVenueList
        venues={city?.venues || []}
        nearbyVenues={nearbyVenues || []}
        setListHoveredVenueId={setListHoveredVenueId}
        listHoveredVenueId={listHoveredVenueId}
        city={city}
      />
    ),
    courtMapProps,
    handleNavigateToCourtFinder,
    distance,
    setDistance,
    onAddressChange: ({ latitude, longitude }: { latitude: number; longitude: number }) => {
      handleNavigateToCourtFinder({ lat: latitude, lng: longitude });
    },
  };
};

export default useCityVenues;
